import { openDB, type IDBPDatabase } from 'idb';
import type { DocumentoForense } from '@/components/editor-forense';

import { getAtuacao } from '../delegacao/atuacao';
import { getOutboxDbName, getUserIdOrNull } from './userScope';

const DB_VERSION = 1;
const STORE_NAME = 'entries';

export type OutboxType =
  | 'peticao'
  | 'peticao_full_flow'
  | 'upload'
  | 'anotacao'
  | 'comunicacao_lida'
  | 'comunicacao_todas_lidas'
  | 'evento_criar'
  | 'evento_editar'
  | 'evento_deletar'
  | 'documento_deletar'
  | 'documento_editar'
  | 'modelo_criar'
  | 'modelo_editar'
  | 'modelo_deletar'
  | 'processo_criar'
  | 'processo_editar'
  | 'cliente_criar'
  | 'cliente_editar'
  | 'cliente_deletar'
  // Conjunto de tags de um registro (PUT idempotente) e aplicação em lote (PLANO_TAGS D12).
  | 'tags_aplicar'
  // Upload eager de petição (operações granulares por petição). Toda
  // mutação no editor de petição passa pelo outbox para preservar
  // intenção mesmo se a aba fechar durante o upload.
  | 'upload_anexo_peticao'
  | 'upload_principal_peticao'
  | 'patch_anexo_peticao'
  | 'delete_anexo_peticao'
  | 'reordenar_anexos_peticao'
  | 'patch_rascunho_peticao'
  | 'submit_peticao_outbox'
  | 'secretario_retorno'
  | 'secretario_assumir'
  | 'secretario_desfecho'
  | 'secretario_devolver'
  | 'secretario_ignorar_numero';

/**
 * Estados de uma entry. ``blocked`` é introduzido para a feature de upload
 * eager: quando uma entry de uma petição vira ``failed``, todas as outras
 * dependentes (mesma ``peticao_id``) viram ``blocked`` e só voltam a
 * ``pending`` quando a entry com falha é cancelada/revertida.
 */
export type OutboxStatus = 'pending' | 'syncing' | 'done' | 'failed' | 'blocked';

/**
 * Metadata for the petition full-flow entry stored in the outbox.
 * Sequence handled by syncManager: createRascunho → patchRascunho →
 * upload principal (if UPLOAD_PDF) → upload each anexo → submit.
 */
export interface PeticaoFullFlowPayload {
  type: 'peticao_full_flow';
  draft_key: string;
  idempotency_key: string;
  processo_id: string;
  /**
   * Tramitação escolhida no wizard quando o processo é multi-grau. Opcional:
   * quando omitido, o backend resolve para a tramitação atual (legado).
   */
  id_fonte_dados_codex?: string;
  metadata: {
    modo_corpo: 'EDITOR' | 'UPLOAD_PDF';
    corpo_html?: string | null;
    /**
     * Tipo do documento principal (lista do tribunal). O id_tipo_peticao
     * (catálogo TPU/SGT) não viaja no payload — o backend resolve com
     * default fixo, espelhando o portal CNJ.
     */
    id_tipo_documento_principal: number;
    /**
     * A folha do editor forense e a proveniência do texto (Bloco 3 do
     * PLANO_BANCADA). Opcionais: as entradas gravadas antes seguem válidas, e sem
     * folha o corpo segue o caminho antigo de renderização.
     */
    documento?: DocumentoForense;
    origem_ia?: boolean;
    mensagem_ia_origem_id?: string | null;
  };
  anexos: Array<{
    ordem: number;
    id_tipo_documento: number;
    descricao?: string | null;
    nome: string;
    content_type: string;
    blob_ref: string;
    sigiloso?: boolean;
  }>;
  /** Only present when modo_corpo = UPLOAD_PDF. */
  principal_file?: {
    blob_ref: string;
    nome: string;
    content_type: string;
  };
}

/**
 * Legacy generic payload shape (default for every entry type besides
 * peticao_full_flow). The sync manager replays it by re-issuing the HTTP
 * request with optional blobs as multipart.
 */
export interface GenericHttpPayload {
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url?: string;
  body?: unknown;
  blobs?: Array<{ name: string; data: Blob; mime: string }>;
  /**
   * Ordem CAUSAL: entries com o mesmo grupo são drenadas em FIFO e a primeira
   * falha segura as seguintes até a próxima rodada (como as de uma petição).
   * Sem grupo, cada entry é independente — um DELETE que falhou por rede não
   * impediria o PUT posterior da mesma coisa de passar na frente dele.
   */
  grupo?: string;
  /**
   * Falha TRANSITÓRIA (rede, 5xx, 401/403) não esgota tentativas: a entry volta a
   * `pending` para sempre, e só uma recusa determinística (4xx) a encerra. Para
   * gesto pequeno e idempotente cujo descarte perderia o que o usuário escreveu
   * (anotações): sete minutos de backend fora do ar não podem apagar uma nota.
   */
  semTetoDeTentativas?: boolean;
}

/**
 * Identificação de um anexo num PATCH/DELETE/reordenação enfileirado.
 *
 * - Anexos já confirmados pelo backend têm ``anexo_id`` real.
 * - Anexos que ainda nem chegaram a confirmar (entry ``upload_anexo_peticao``
 *   pendente) só têm ``client_upload_id``. O handler do sync resolve para o
 *   ``anexo_id`` real consultando o backend pelo ``client_upload_id``.
 */
export interface AnexoRef {
  anexo_id?: string;
  client_upload_id?: string;
}

export interface UploadAnexoPeticaoPayload {
  peticao_id: string;
  client_upload_id: string;
  blob_ref: string;
  ordem: number;
  id_tipo_documento: number;
  descricao?: string | null;
  sigiloso?: boolean;
  nome: string;
  content_type: string;
  tipo_documento_descricao?: string | null;
}

export interface UploadPrincipalPeticaoPayload {
  peticao_id: string;
  client_upload_id: string;
  blob_ref: string;
  nome: string;
  content_type: string;
}

export interface PatchAnexoPeticaoPayload extends AnexoRef {
  peticao_id: string;
  patch: {
    id_tipo_documento?: number;
    tipo_documento_descricao?: string | null;
    descricao?: string | null;
    sigiloso?: boolean;
  };
}

export interface DeleteAnexoPeticaoPayload extends AnexoRef {
  peticao_id: string;
}

export interface ReordenarAnexosPeticaoPayload {
  peticao_id: string;
  ordens: Array<AnexoRef & { ordem: number }>;
}

export interface PatchRascunhoPeticaoPayload {
  peticao_id: string;
  patch: {
    modo_corpo?: 'EDITOR' | 'UPLOAD_PDF';
    corpo_html?: string | null;
    /** Folha do editor forense: marca o corpo no formato dele (docs/PLANO_EDITOR_TELAS.md). */
    documento?: DocumentoForense;
    id_tipo_documento_principal?: number;
    tipo_documento_principal_descricao?: string | null;
    /** Proveniência de IA (F3): corpo veio de minuta do Chat de IA. */
    origem_ia?: boolean;
    mensagem_ia_origem_id?: string | null;
  };
}

export interface SubmitPeticaoOutboxPayload {
  peticao_id: string;
  idempotency_key: string;
}

/**
 * Union-friendly payload shape: the legacy HTTP fields remain top-level
 * (preserving the existing test contract that reads `payload.method` directly),
 * while the petition full-flow fields are carried as optional metadata keys.
 *
 * The sync manager uses `entry.type === 'peticao_full_flow'` to route,
 * then casts to PeticaoFullFlowPayload when needed.
 */
export type OutboxPayload = GenericHttpPayload &
  Partial<PeticaoFullFlowPayload> &
  Partial<UploadAnexoPeticaoPayload> &
  Partial<UploadPrincipalPeticaoPayload> &
  Partial<PatchAnexoPeticaoPayload> &
  Partial<DeleteAnexoPeticaoPayload> &
  Partial<ReordenarAnexosPeticaoPayload> &
  Partial<PatchRascunhoPeticaoPayload> &
  Partial<SubmitPeticaoOutboxPayload>;

export interface OutboxEntry {
  id: string;
  createdAt: string;
  type: OutboxType;
  status: OutboxStatus;
  retryCount: number;
  payload: OutboxPayload;
  error?: string;
  description: string;
  /**
   * Titular em cuja conta a ação foi feita (contexto delegado); ausente = a própria
   * conta. Carimbado no enfileiramento e respeitado na reprodução: a entry só é
   * sincronizada quando o contexto ativo é o mesmo (ver ``syncManager``).
   */
  atuandoPor?: string | null;
}

type NovaEntry = Omit<OutboxEntry, 'id' | 'createdAt' | 'status' | 'retryCount' | 'atuandoPor'>;

/**
 * Na conta de outra pessoa não se trabalha offline (P11). O outbox continua servindo o
 * caminho ONLINE (a tela de peticionar enfileira e drena na hora), mas nada da conta do
 * titular fica esperando conexão no aparelho do delegado.
 */
function exigirConexaoNaContaDeOutro(): void {
  if (getAtuacao() && typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Sem conexão. Na conta de outra pessoa, as ações não ficam salvas offline.');
  }
}

/** Contagens da interface: só o contexto ativo (pendência da conta do titular não é do delegado). */
function doContextoAtivo(e: OutboxEntry): boolean {
  return (e.atuandoPor ?? null) === (getAtuacao()?.titularId ?? null);
}

let dbInstance: IDBPDatabase | null = null;
let currentDbName: string | null = null;

async function getDB(): Promise<IDBPDatabase> {
  const dbName = getOutboxDbName();

  // Se o DB name mudou (troca de usuário), fechar conexão anterior
  if (dbInstance && currentDbName !== dbName) {
    dbInstance.close();
    dbInstance = null;
  }

  if (dbInstance) return dbInstance;

  currentDbName = dbName;
  dbInstance = await openDB(dbName, DB_VERSION, {
    upgrade(db) {
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      store.createIndex('by-status', 'status');
      store.createIndex('by-created', 'createdAt');
    },
  });

  return dbInstance;
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

// `createdAt` é a ordem FIFO do sync (índice `by-created`). Duas entries no
// mesmo milissegundo empatariam — e o IndexedDB não garante a ordem do empate:
// um DELETE podia ser drenado antes do PUT que o precedeu. Estritamente
// crescente dentro da sessão.
let ultimoCreatedMs = 0;

function createdAtMonotonico(): string {
  ultimoCreatedMs = Math.max(Date.now(), ultimoCreatedMs + 1);
  return new Date(ultimoCreatedMs).toISOString();
}

/**
 * Adiciona uma entrada ao outbox para sincronização posterior.
 * Retorna o ID gerado.
 * Lança erro se não houver userId definido (requer usuário autenticado).
 */
export async function addEntry(entry: NovaEntry): Promise<string> {
  if (!getUserIdOrNull()) {
    throw new Error('Não é possível enfileirar ação offline sem usuário autenticado');
  }
  exigirConexaoNaContaDeOutro();
  try {
    const db = await getDB();
    const id = generateId();
    const fullEntry: OutboxEntry = {
      ...entry,
      id,
      createdAt: createdAtMonotonico(),
      status: 'pending',
      retryCount: 0,
      atuandoPor: getAtuacao()?.titularId ?? null,
    };
    await db.put(STORE_NAME, fullEntry);
    notifyListeners();
    return id;
  } catch (err) {
    throw new Error(`Falha ao adicionar entrada ao outbox: ${String(err)}`);
  }
}

/**
 * Retorna todas as entradas pendentes em ordem FIFO (mais antiga primeiro).
 * Retorna vazio se userId não definido.
 */
export async function getPendingEntries(): Promise<OutboxEntry[]> {
  if (!getUserIdOrNull()) return [];
  try {
    const db = await getDB();
    const all = await db.getAllFromIndex(STORE_NAME, 'by-created');
    return all.filter(
      (e: OutboxEntry) => e.status === 'pending',
    ) as OutboxEntry[];
  } catch {
    return [];
  }
}

/**
 * Retorna a quantidade de entradas pendentes ou em sincronização.
 * Retorna 0 se userId não definido.
 */
export async function getPendingCount(): Promise<number> {
  if (!getUserIdOrNull()) return 0;
  try {
    const db = await getDB();
    const all = await db.getAll(STORE_NAME);
    return (all as OutboxEntry[]).filter(
      (e) => (e.status === 'pending' || e.status === 'syncing') && doContextoAtivo(e),
    ).length;
  } catch {
    return 0;
  }
}

/**
 * Retorna a quantidade de entradas com falha definitiva.
 * Retorna 0 se userId não definido.
 */
export async function getFailedCount(): Promise<number> {
  if (!getUserIdOrNull()) return 0;
  try {
    const db = await getDB();
    const all = await db.getAll(STORE_NAME);
    return (all as OutboxEntry[]).filter(
      (e) => e.status === 'failed' && doContextoAtivo(e),
    ).length;
  } catch {
    return 0;
  }
}

/**
 * Atualiza o status de uma entrada. Incrementa retryCount ao voltar para 'pending' com erro.
 */
export async function updateEntryStatus(
  id: string,
  status: OutboxStatus,
  error?: string,
): Promise<void> {
  try {
    const db = await getDB();
    const entry = (await db.get(STORE_NAME, id)) as OutboxEntry | undefined;
    if (!entry) return;

    entry.status = status;

    if (error) {
      entry.error = error;
    }

    // Incrementar retryCount quando volta de syncing para pending (falha recuperável)
    if (status === 'pending' && entry.retryCount !== undefined) {
      entry.retryCount += 1;
    }

    await db.put(STORE_NAME, entry);
    notifyListeners();
  } catch {
    // Falha silenciosa — não bloquear o fluxo de sync
  }
}

/**
 * Atualiza o ``payload`` de uma entrada in-place. Usado pra coalescer
 * mutações (ex.: editar metadata de anexo ainda pendente, ou empilhar
 * patches do rascunho na mesma entry quando faz sentido).
 *
 * Não toca em ``status`` nem ``retryCount`` — só substitui o payload.
 */
export async function updateEntryPayload(
  id: string,
  mutator: (payload: OutboxPayload) => OutboxPayload,
): Promise<void> {
  try {
    const db = await getDB();
    const entry = (await db.get(STORE_NAME, id)) as OutboxEntry | undefined;
    if (!entry) return;
    entry.payload = mutator(entry.payload);
    await db.put(STORE_NAME, entry);
    notifyListeners();
  } catch {
    // Falha silenciosa
  }
}

/** Recupera uma entry por id. */
export async function getEntry(id: string): Promise<OutboxEntry | null> {
  if (!getUserIdOrNull()) return null;
  try {
    const db = await getDB();
    const entry = (await db.get(STORE_NAME, id)) as OutboxEntry | undefined;
    return entry ?? null;
  } catch {
    return null;
  }
}

/**
 * Remove uma entry por id. Operação CRUD pura — não conhece XHR/abort.
 * Para cancelar uma entry em ``syncing`` (que pode ter um upload em curso),
 * use ``cancelSyncingEntry`` no syncManager, que orquestra o ciclo de vida.
 */
export async function removeEntry(id: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete(STORE_NAME, id);
    notifyListeners();
  } catch {
    // Falha silenciosa
  }
}

/**
 * Substitui a última entry pendente que casa com ``predicate`` removendo-a
 * e adicionando uma nova ao fim da fila. Mantém ordem causal: a entrada
 * coalescida vai depois de qualquer dependência criada nesse meio tempo.
 *
 * Usado em ``reordenar_anexos_peticao`` e ``patch_rascunho_peticao``, onde
 * coalescer in-place poderia colocar a entry antes de uploads que vieram
 * depois dela.
 */
export async function replaceEntryAtTail(
  predicate: (entry: OutboxEntry) => boolean,
  newEntry: NovaEntry,
): Promise<string> {
  if (!getUserIdOrNull()) {
    throw new Error('Não é possível enfileirar ação offline sem usuário autenticado');
  }
  exigirConexaoNaContaDeOutro();
  const db = await getDB();
  const all = (await db.getAllFromIndex(STORE_NAME, 'by-created')) as OutboxEntry[];
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  for (const e of all) {
    if (
      (e.status === 'pending' || e.status === 'blocked') &&
      predicate(e)
    ) {
      store.delete(e.id);
    }
  }
  const id = generateId();
  const fullEntry: OutboxEntry = {
    ...newEntry,
    id,
    createdAt: new Date().toISOString(),
    status: 'pending',
    retryCount: 0,
    atuandoPor: getAtuacao()?.titularId ?? null,
  };
  store.put(fullEntry);
  await tx.done;
  notifyListeners();
  return id;
}

/**
 * Marca todas as entries ``pending``/``syncing`` da petição como
 * ``blocked``. Chamado quando uma entry da petição vira ``failed`` —
 * dependentes só rodam após a falha ser revertida ou cancelada.
 */
export async function markBlockedByPeticao(
  peticaoId: string,
  reason?: string,
): Promise<void> {
  try {
    const db = await getDB();
    const all = (await db.getAll(STORE_NAME)) as OutboxEntry[];
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    for (const entry of all) {
      if (
        (entry.status === 'pending' || entry.status === 'syncing') &&
        entry.payload.peticao_id === peticaoId
      ) {
        entry.status = 'blocked';
        if (reason) entry.error = reason;
        store.put(entry);
      }
    }
    await tx.done;
    notifyListeners();
  } catch {
    // Falha silenciosa
  }
}

/**
 * Reverte ``blocked`` para ``pending`` para todas as entries da petição.
 * Chamado quando a entry com falha é cancelada/revertida pelo usuário.
 */
export async function unblockPeticao(peticaoId: string): Promise<void> {
  try {
    const db = await getDB();
    const all = (await db.getAll(STORE_NAME)) as OutboxEntry[];
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    for (const entry of all) {
      if (entry.status === 'blocked' && entry.payload.peticao_id === peticaoId) {
        entry.status = 'pending';
        entry.error = undefined;
        store.put(entry);
      }
    }
    await tx.done;
    notifyListeners();
  } catch {
    // Falha silenciosa
  }
}

/**
 * Lista todas as entries (em qualquer status) que pertencem a uma petição.
 * Usado pela barra do protocolo nos autos para renderizar o estado em transit.
 */
export async function listEntriesByPeticao(peticaoId: string): Promise<OutboxEntry[]> {
  if (!getUserIdOrNull()) return [];
  try {
    const db = await getDB();
    const all = (await db.getAllFromIndex(STORE_NAME, 'by-created')) as OutboxEntry[];
    return all.filter((e) => e.payload.peticao_id === peticaoId);
  } catch {
    return [];
  }
}

/**
 * Remove todas as entradas com status 'done'.
 */
export async function removeCompletedEntries(): Promise<void> {
  try {
    const db = await getDB();
    const all = (await db.getAll(STORE_NAME)) as OutboxEntry[];
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    for (const entry of all) {
      if (entry.status === 'done') {
        store.delete(entry.id);
      }
    }

    await tx.done;
    notifyListeners();
  } catch {
    // Falha silenciosa
  }
}

/**
 * Retorna todas as entradas, ordenadas por createdAt decrescente (mais recentes primeiro).
 * Retorna vazio se userId não definido.
 */
export async function getAllEntries(): Promise<OutboxEntry[]> {
  try {
    return await getAllEntriesOrThrow();
  } catch {
    return [];
  }
}

/**
 * Como `getAllEntries`, mas uma falha de leitura do IndexedDB SOBE em vez de
 * virar lista vazia. Para quem sobrepõe a fila a dados do servidor: ali, "não
 * consegui ler a fila" e "a fila está vazia" levam a telas diferentes — tratar a
 * primeira como a segunda apaga da tela o gesto que ainda não sincronizou.
 */
export async function getAllEntriesOrThrow(): Promise<OutboxEntry[]> {
  if (!getUserIdOrNull()) return [];
  const db = await getDB();
  const all = (await db.getAllFromIndex(STORE_NAME, 'by-created')) as OutboxEntry[];
  return all.reverse();
}

// --- Pub/Sub para notificar componentes sobre mudanças ---

type OutboxListener = () => void;
const listeners: Set<OutboxListener> = new Set();

export function subscribe(listener: OutboxListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyListeners(): void {
  listeners.forEach((fn) => fn());
}

/**
 * Fecha a conexão singleton com o banco do outbox e reseta a referência.
 * Deve ser chamado antes de deletar o banco para evitar bloqueio.
 */
export async function closeOutbox(): Promise<void> {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}
